import { ReactNode, useState } from 'react';

import * as api from '@shared/api/giftPartner/giftPartner.controller';
import { useGiftPartnerAction } from '@shared/api/giftPartner/giftPartner.hook';
import {
  GiftPartner,
  GiftPartnerBrand,
  GiftPartnerWebhookEventType,
} from '@shared/api/giftPartner/giftPartner.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { LowerRole } from '@shared/types';
import { Alert, Button, Checkbox, Form, Input, Modal, Radio, Select, Tag } from 'antd';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';

import { GiftPartnerApplicantGuide } from './GiftPartnerApplicantGuide';
import { GiftPartnerGuideResources } from './guide/giftPartnerGuideLinks';
import {
  BrandChoices,
  ConnectionFields,
  ContactFields,
  ContactForm,
  PartnerError,
  PartnerSecret,
  PartnerSection,
  SecretModal,
} from './GiftPartnerForms';
import {
  brandIds,
  dateLabel,
  hookUrlError,
  OverviewBrand,
  partnerStatus,
} from './giftPartner.utils';

const eventOptions: { value: GiftPartnerWebhookEventType; label: string }[] = [
  { value: 'delivery.created', label: '발주' },
  { value: 'delivery.address_submitted', label: '배송지 입력' },
  { value: 'delivery.address_updated', label: '배송지 변경' },
  { value: 'delivery.canceled', label: '취소' },
];
const brandName = (id: string, brands: GiftPartnerBrand[], partner: GiftPartner) =>
  brands.find((b) => b._id === id)?.name ??
  partner.giftItemBrands.find(
    (b): b is GiftPartnerBrand => typeof b !== 'string' && b._id === id,
  )?.name ??
  id;

export const PendingChanges = ({
  partner,
  brands,
}: {
  partner: GiftPartner;
  brands: GiftPartnerBrand[];
}) => {
  const change = partner.pendingChange;
  if (!change) return null;
  const current = brandIds(partner);
  return (
    <div className="space-y-3 rounded border border-amber-300 bg-amber-50 p-4">
      <h3 className="font-semibold">변경 요청 승인 대기 중</h3>
      <p className="text-sm">
        승인 전까지 기존 키와 연결 정보가 유지됩니다. 새 브랜드의 발주는 API·웹훅으로
        나가지 않으며, 제외 요청한 브랜드의 발주는 계속 전달됩니다.
      </p>
      <div className="grid min-w-0 gap-4 sm:grid-cols-2">
        <div className="min-w-0 space-y-2 break-words">
          <strong>현재 적용 값</strong>
          {change.giftItemBrands && (
            <p>
              브랜드: {current.map((id) => brandName(id, brands, partner)).join(', ')}
            </p>
          )}
          {change.allowedIps && <p>IP: {partner.allowedIps.join(', ')}</p>}
          {change.webhookUrl !== undefined && (
            <p className="break-all">URL: {partner.webhook?.url || '미등록'}</p>
          )}
        </div>
        <div className="min-w-0 space-y-2 break-words">
          <strong>요청 값 · {dateLabel(change.requestedAt)}</strong>
          {change.giftItemBrands && (
            <>
              <p>
                브랜드:{' '}
                {change.giftItemBrands
                  .map((id) => brandName(id, brands, partner))
                  .join(', ')}
              </p>
              {change.giftItemBrands
                .filter((id) => !current.includes(id))
                .map((id) => (
                  <Tag key={id} color="green">
                    +{brandName(id, brands, partner)}
                  </Tag>
                ))}
              {current
                .filter((id) => !change.giftItemBrands?.includes(id))
                .map((id) => (
                  <Tag key={id} color="red">
                    −{brandName(id, brands, partner)}
                  </Tag>
                ))}
            </>
          )}
          {change.allowedIps && <p>IP: {change.allowedIps.join(', ')}</p>}
          {change.webhookUrl !== undefined && (
            <p className="break-all">URL: {change.webhookUrl || '미등록'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

type EditMode =
  | 'brands'
  | 'connection'
  | 'contact'
  | 'admin'
  | 'webhook'
  | 'reject'
  | 'issue';
export const GiftPartnerDetail = ({
  partner,
  brands,
  role,
  backPath,
}: {
  partner: GiftPartner;
  brands: OverviewBrand[];
  role: api.GiftPartnerRole;
  backPath: string;
}) => {
  const admin = role === LowerRole.Admin;
  const approved = partner.status === 'Approved';
  const canOperate = approved && (admin || partner.isEnabled);
  const canManageKeys = partner.status === 'Requested' || canOperate;
  const canEdit =
    admin || partner.status === 'Requested' || (approved && partner.isEnabled);
  const { run, busy, error } = useGiftPartnerAction();
  const [edit, setEdit] = useState<EditMode>();
  const [secret, setSecret] = useState<PartnerSecret>();
  const [notice, setNotice] = useState('');
  const [dismissedReview, setDismissedReview] = useState('');
  const [confirmation, setConfirmation] = useState<{
    title: string;
    message: string;
    task: () => Promise<void>;
  }>();
  const [ping, setPing] = useState<{ ok: boolean; text: string }>();
  const [pingLoading, setPingLoading] = useState(false);
  const activeKeys = partner.apiKeys.filter((key) => !key.revokedAt).length;
  const complete = (message: string) => {
    setEdit(undefined);
    setConfirmation(undefined);
    setNotice(message);
  };
  const reveal = (title: string, value: string) =>
    setSecret({
      title,
      value,
      partnerName: partner.name,
      usageNotice:
        partner.status === 'Requested'
          ? '미리 발급한 키는 Admin 승인 후부터 사용할 수 있습니다. 승인 전 API 호출은 거부됩니다.'
          : undefined,
    });
  const reviewKey = `${partner.reviewedAt}:${partner.rejectReason}`;
  const keySection = (
    <PartnerSection title="API 키 관리">
      <p className="text-sm text-gray-600">
        API 키는 개발 담당자가 서버에 등록하는 인증 정보입니다. 신청 1건에 포함된 모든
        브랜드를 같은 키로 조회합니다.
      </p>
      {partner.status === 'Requested' && (
        <Alert
          type="info"
          message="키를 미리 발급해 서버에 설정할 수 있습니다. Admin 승인 전에는 사용할 수 없습니다."
        />
      )}
      {(admin || activeKeys > 0 || !canManageKeys) && (
        <Button
          type={admin ? 'primary' : 'default'}
          className={admin ? 'gift-partner-primary' : undefined}
          disabled={!canManageKeys || activeKeys >= 2 || busy}
          onClick={() => setEdit('issue')}
        >
          {activeKeys ? '키 추가 발급' : 'API 키 발급받기'}
        </Button>
      )}
      {activeKeys >= 2 && (
        <p className="text-sm text-amber-700">
          폐기하지 않은 키는 2개까지입니다. 하나를 폐기한 뒤 발급하세요.
        </p>
      )}
      {partner.apiKeys.length === 0 && (
        <p className="text-sm text-gray-500">
          {canManageKeys
            ? '아직 발급받은 API 키가 없습니다. ‘API 키 발급받기’를 눌러 진행하세요.'
            : '발급받은 API 키가 없습니다. 현재 상태에서는 키를 발급할 수 없습니다.'}
        </p>
      )}
      <div className="space-y-2">
        {partner.apiKeys.map((key) => (
          <div
            key={key._id}
            className={`flex flex-wrap items-center justify-between gap-3 rounded border p-3 ${
              key.revokedAt ? 'bg-gray-50 text-gray-500' : 'border-gray-200'
            }`}
          >
            <div className="min-w-0 space-y-1 text-sm">
              <p className="break-all font-mono">gbk_{key.prefix}_••••••••</p>
              <p>
                {key.label || '이름 없음'} ·{' '}
                {key.revokedAt
                  ? `폐기됨 (${dateLabel(key.revokedAt)})`
                  : !approved
                  ? '승인 전 · 사용 불가'
                  : !partner.isEnabled
                  ? '중지됨 · 사용 불가'
                  : '사용 가능'}
              </p>
              <p>발급 {dateLabel(key.createdAt)}</p>
              <p>
                마지막 사용{' '}
                {key.lastUsedAt
                  ? `${dateLabel(key.lastUsedAt)} · ${key.lastUsedIp ?? ''}`
                  : '사용 전'}
              </p>
            </div>
            {!key.revokedAt && (
              <Button
                danger
                disabled={!canManageKeys || busy}
                onClick={() =>
                  setConfirmation({
                    title: '연동키 폐기',
                    message: approved
                      ? `gbk_${key.prefix}_•••••••• 키로 호출 중인 서버는 즉시 인증에 실패합니다. 폐기 후 되돌릴 수 없습니다.`
                      : `gbk_${key.prefix}_•••••••• 키를 폐기하면 승인 후에도 사용할 수 없습니다. 폐기 후 되돌릴 수 없습니다.`,
                    task: async () => {
                      await api.revokeGiftPartnerKey(role, partner._id, key._id);
                    },
                  })
                }
              >
                폐기
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-sm text-gray-500">
        분실 시 기존 키를 폐기한 뒤 발급하세요. 무중단 교체: 새 키 발급 → 서버에 적용 → 옛
        키의 마지막 사용이 멈춘 것을 확인 → 옛 키 폐기.
      </p>
    </PartnerSection>
  );

  return (
    <div className="space-y-4">
      <div className="sticky top-12 z-20 space-y-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm lg:top-0">
        <Link to={backPath} className="text-sm text-indigo-700">
          ← 연동 목록
        </Link>
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="break-all text-xl font-bold">{partner.name}</h1>
          <Tag color={partner.isEnabled ? 'green' : 'default'}>
            {partnerStatus(partner)}
          </Tag>
        </div>
        <div className="flex flex-wrap gap-1">
          {brandIds(partner).map((id) => (
            <Tag key={id} className="max-w-full whitespace-normal break-all">
              {brandName(id, brands, partner)}
            </Tag>
          ))}
        </div>
      </div>
      <PartnerError error={error} />
      {notice && (
        <Alert
          type="success"
          message={notice}
          showIcon
          closable
          onClose={() => setNotice('')}
        />
      )}
      {!admin && approved && !partner.isEnabled && (
        <Alert
          type="warning"
          message={
            <>
              연동이 중지되었습니다. <Link to="/gift-brand/enquiry">문의하기</Link>로
              연락해 주세요.
            </>
          }
        />
      )}
      <PendingChanges partner={partner} brands={brands} />
      {partner.rejectReason &&
        !partner.pendingChange &&
        dismissedReview !== reviewKey && (
          <Alert
            type="warning"
            closable
            onClose={() => setDismissedReview(reviewKey)}
            message={`${
              partner.status === 'Rejected'
                ? '신청이 반려되었습니다'
                : '최근 변경 요청이 반려되었습니다'
            }: ${partner.rejectReason}`}
            description={dateLabel(partner.reviewedAt)}
          />
        )}
      {partner.status === 'Rejected' && !admin && (
        <Link
          className="inline-block text-indigo-700 underline"
          to={`${backPath}/new?retry=${partner._id}`}
        >
          다시 신청
        </Link>
      )}

      {!admin && (
        <GiftPartnerApplicantGuide
          partner={partner}
          busy={busy}
          onIssue={() => setEdit('issue')}
        />
      )}
      {!admin && (partner.apiKeys.length > 0 || !canManageKeys) && keySection}

      <PartnerSection title="연결 정보">
        <dl className="grid min-w-0 gap-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">허용 IP</dt>
            <dd className="break-all font-medium">
              {partner.allowedIps.join(', ') || '미등록'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">콜백 URL</dt>
            <dd className="break-all font-medium">{partner.webhook?.url || '미등록'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">개발 담당자</dt>
            <dd className="break-all">
              {partner.contact?.name || '미등록'} ·{' '}
              {partner.contact?.phoneNumber || '전화번호 미등록'}
              <br />
              {partner.contact?.email}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">신청일 (한국 시간)</dt>
            <dd>{dateLabel(partner.requestedAt ?? partner.createdAt)}</dd>
          </div>
        </dl>
        <div className="flex flex-wrap gap-2">
          {!admin && (
            <>
              <Button disabled={!canEdit || busy} onClick={() => setEdit('brands')}>
                브랜드 편집
              </Button>
              <Button disabled={!canEdit || busy} onClick={() => setEdit('connection')}>
                {approved ? 'IP·콜백 URL 변경 요청' : '신청 정보 수정'}
              </Button>
              <Button disabled={!canEdit || busy} onClick={() => setEdit('contact')}>
                담당자 수정
              </Button>
            </>
          )}
          {admin && (
            <>
              <Button disabled={busy} onClick={() => setEdit('admin')}>
                연동 정보 직접 수정
              </Button>
              <Button disabled={busy} onClick={() => setEdit('webhook')}>
                웹훅 설정
              </Button>
              <Button
                disabled={!approved || busy}
                danger={partner.isEnabled}
                onClick={() =>
                  setConfirmation({
                    title: partner.isEnabled ? '연동 중지' : '연동 재개',
                    message: partner.isEnabled
                      ? '이 서버의 API 호출과 웹훅 발송이 즉시 중단됩니다.'
                      : '이 서버의 API 연동을 다시 활성화합니다.',
                    task: async () => {
                      await api.updateGiftPartner(partner._id, {
                        isEnabled: !partner.isEnabled,
                      });
                    },
                  })
                }
              >
                {partner.isEnabled ? '중지' : '재개'}
              </Button>
            </>
          )}
        </div>
        {admin && partner.memo && (
          <p className="whitespace-pre-wrap break-words text-sm text-gray-600">
            관리자 메모: {partner.memo}
          </p>
        )}
      </PartnerSection>

      {admin && (partner.status === 'Requested' || partner.pendingChange) && (
        <PartnerSection title="신청 검토">
          <Alert
            type="warning"
            showIcon
            message="배송관리자 계정에는 2차 인증이 없습니다. 담당자에게 전화해 본인이 신청한 것이 맞는지, IP·URL이 맞는지 확인한 뒤 승인하세요."
          />
          <p className="text-sm text-gray-600">
            승인하면 미리 발급한 키를 사용할 수 있습니다. 승인 시 키는 교체되지 않으며
            웹훅은 신청자가 별도로 설정합니다.
          </p>
          <div className="flex gap-2">
            <Button
              type="primary"
              className="gift-partner-primary"
              disabled={busy}
              onClick={() =>
                setConfirmation({
                  title: '신청 승인',
                  message:
                    '담당자와 신청 내용 및 데이터 전송 범위를 확인했습니까? 승인하면 요청 값이 적용됩니다.',
                  task: async () => {
                    await api.approveGiftPartner(partner._id);
                  },
                })
              }
            >
              승인
            </Button>
            <Button danger disabled={busy} onClick={() => setEdit('reject')}>
              반려
            </Button>
          </div>
        </PartnerSection>
      )}

      {admin && keySection}

      <ApplicantWebhookSection collapsed={!admin && !approved}>
        <PartnerSection title="웹훅">
          {!approved && (
            <p className="text-sm text-gray-500">
              웹훅은 Admin 승인 후 이용할 수 있습니다.
            </p>
          )}
          {!partner.webhook?.url && (
            <p className="text-sm">
              콜백 URL이 등록되지 않았습니다.{' '}
              {canEdit && (
                <Button
                  size="small"
                  onClick={() => setEdit(admin ? 'webhook' : 'connection')}
                >
                  {admin ? 'URL 등록' : '변경 요청으로 등록'}
                </Button>
              )}
            </p>
          )}
          <p className="text-sm">
            알림 수신: <strong>{partner.webhook?.isEnabled ? '사용 중' : '꺼짐'}</strong>
          </p>
          {partner.webhook?.lastError ? (
            <Alert
              type="error"
              message={`최근 발송 실패 · ${dateLabel(partner.webhook.lastFailureAt)}`}
              description={partner.webhook.lastError}
            />
          ) : partner.webhook?.lastSuccessAt ? (
            <p className="text-sm text-green-700">
              정상 · 마지막 성공 {dateLabel(partner.webhook.lastSuccessAt)}
            </p>
          ) : null}
          <p className="text-sm text-gray-600">
            구독 이벤트:{' '}
            {partner.webhook?.events?.length
              ? partner.webhook.events
                  .map((e) => eventOptions.find((o) => o.value === e)?.label ?? e)
                  .join(', ')
              : '전체'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={
                !canOperate ||
                busy ||
                (!partner.webhook?.isEnabled && !partner.webhook?.url)
              }
              onClick={() =>
                void run(
                  async () => {
                    const result = await api.setGiftPartnerWebhook(role, partner._id, {
                      isEnabled: !partner.webhook?.isEnabled,
                    });
                    if (result.webhookSecret)
                      reveal('웹훅 서명 secret', result.webhookSecret);
                  },
                  () => complete('웹훅 설정을 저장했습니다.'),
                )
              }
            >
              {partner.webhook?.isEnabled ? '웹훅 끄기' : '웹훅 켜기'}
            </Button>
            <Button disabled={!canOperate || busy} onClick={() => setEdit('webhook')}>
              구독 이벤트 설정
            </Button>
            <Button
              disabled={!canOperate || !partner.webhook?.url || busy}
              onClick={() =>
                setConfirmation({
                  title: '웹훅 secret 재발급',
                  message:
                    '즉시 교체됩니다. 공급사 서버에 새 secret을 넣기 전까지 서명 검증이 실패합니다.',
                  task: async () => {
                    const result = await api.rotateGiftPartnerWebhookSecret(
                      role,
                      partner._id,
                    );
                    reveal('웹훅 서명 secret', result.webhookSecret);
                  },
                })
              }
            >
              secret 재발급
            </Button>
            <Button
              disabled={!canOperate || !partner.webhook?.url || busy}
              loading={pingLoading}
              onClick={() => {
                setPingLoading(true);
                setPing(undefined);
                void run(
                  async () => {
                    try {
                      const result = await api.pingGiftPartnerWebhook(role, partner._id);
                      setPing({
                        ok: result.ok,
                        text: result.ok
                          ? `수신 확인 (HTTP ${result.status ?? '—'})`
                          : `실패: HTTP ${result.status ?? '—'} / ${
                              result.error ?? '알 수 없는 오류'
                            }`,
                      });
                    } finally {
                      setPingLoading(false);
                    }
                  },
                  () => setNotice(''),
                );
              }}
            >
              테스트 발송 (최대 5초)
            </Button>
          </div>
          {ping && <Alert type={ping.ok ? 'success' : 'error'} message={ping.text} />}
        </PartnerSection>
        <WebhookHistory partner={partner} role={role} enabled={canOperate} />
      </ApplicantWebhookSection>
      <GiftPartnerGuideResources />

      <Modal
        okButtonProps={{ className: 'gift-partner-primary' }}
        open={!!edit}
        title={`${partner.name} · ${
          edit === 'brands'
            ? '브랜드 편집'
            : edit === 'connection'
            ? 'IP·콜백 URL'
            : edit === 'contact'
            ? '담당자 수정'
            : edit === 'admin'
            ? '연동 정보 직접 수정'
            : edit === 'reject'
            ? '반려 사유'
            : edit === 'issue'
            ? '키 발급'
            : '웹훅 설정'
        }`}
        footer={null}
        destroyOnClose
        maskClosable={!busy}
        keyboard={!busy}
        closable={!busy}
        onCancel={() => setEdit(undefined)}
      >
        <div className="space-y-4">
          <PartnerError error={error} />
          {(edit === 'brands' || edit === 'connection') && (
            <>
              <Alert
                type="info"
                message={
                  approved
                    ? '관리자 승인 후 반영됩니다. 승인 전까지 현재 값으로 계속 동작합니다.'
                    : '승인 대기 중인 신청서 수정은 즉시 반영됩니다.'
                }
                description={
                  edit === 'brands'
                    ? '새 브랜드의 발주는 승인 전까지 API·웹훅으로 나가지 않습니다. 승인 후 같은 연동키·콜백 URL로 전달되며 item.brandName으로 구분합니다. 제외 요청도 승인 전까지는 계속 전달됩니다.'
                    : '서버 이전 시 새 IP를 기존 IP와 함께 요청하고, 이전이 끝난 뒤 옛 IP를 빼 주세요. 대기 중 다시 요청하면 해당 요청 값을 덮어씁니다.'
                }
              />
              <Form
                layout="vertical"
                disabled={busy}
                initialValues={{
                  giftItemBrands:
                    partner.pendingChange?.giftItemBrands ?? brandIds(partner),
                  allowedIps: partner.pendingChange?.allowedIps ?? partner.allowedIps,
                  webhookUrl:
                    partner.pendingChange?.webhookUrl ?? partner.webhook?.url ?? '',
                }}
                onFinish={(values) =>
                  void run(
                    async () => {
                      await api.requestGiftPartnerChange(
                        partner._id,
                        edit === 'brands'
                          ? { giftItemBrands: values.giftItemBrands }
                          : {
                              allowedIps: values.allowedIps,
                              webhookUrl: values.webhookUrl,
                            },
                      );
                    },
                    () =>
                      complete(
                        approved
                          ? '변경 요청을 보냈습니다. 관리자 승인 후 반영됩니다.'
                          : '신청 내용을 수정했습니다.',
                      ),
                  )
                }
              >
                {edit === 'brands' ? (
                  <Form.Item
                    name="giftItemBrands"
                    rules={[
                      {
                        type: 'array',
                        required: true,
                        min: 1,
                        message: '브랜드를 1개 이상 선택하세요',
                      },
                    ]}
                  >
                    <BrandChoices brands={brands} partnerId={partner._id} />
                  </Form.Item>
                ) : (
                  <ConnectionFields />
                )}
                <Button
                  type="primary"
                  className="gift-partner-primary"
                  htmlType="submit"
                  loading={busy}
                >
                  {approved ? '변경 요청' : '신청서 저장'}
                </Button>
              </Form>
            </>
          )}
          {edit === 'contact' && (
            <ContactForm
              contact={partner.contact}
              busy={busy}
              onSubmit={(contact) =>
                void run(
                  async () => {
                    await api.updateGiftPartnerContact(partner._id, contact);
                  },
                  () => complete('담당자를 저장했습니다.'),
                )
              }
            />
          )}
          {edit === 'admin' && (
            <Form
              layout="vertical"
              disabled={busy}
              initialValues={{ ...partner, giftItemBrands: brandIds(partner) }}
              onFinish={(values) =>
                void run(
                  async () => {
                    await api.updateGiftPartner(partner._id, {
                      name: values.name,
                      giftItemBrands: values.giftItemBrands,
                      allowedIps: values.allowedIps,
                      contact: values.contact,
                      memo: values.memo,
                    });
                  },
                  () => complete('연동 정보를 저장했습니다.'),
                )
              }
            >
              <Alert
                type="warning"
                message="관리자 직접 수정은 즉시 적용됩니다. 데이터 전송 범위를 확인해 주세요."
              />
              <Form.Item
                name="name"
                label="연동 이름"
                rules={[{ required: true, whitespace: true }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="giftItemBrands"
                label="브랜드"
                rules={[{ required: true, type: 'array', min: 1 }]}
              >
                <BrandChoices brands={brands} partnerId={partner._id} />
              </Form.Item>
              <ConnectionFields includeUrl={false} />
              <ContactFields />
              <Form.Item name="memo" label="관리자 메모">
                <Input.TextArea rows={3} />
              </Form.Item>
              <Button
                htmlType="submit"
                type="primary"
                className="gift-partner-primary"
                loading={busy}
              >
                즉시 저장
              </Button>
            </Form>
          )}
          {edit === 'webhook' && (
            <WebhookForm
              partner={partner}
              admin={admin}
              busy={busy}
              onSubmit={(dto) =>
                void run(
                  async () => {
                    const result = await api.setGiftPartnerWebhook(
                      role,
                      partner._id,
                      dto,
                    );
                    if (result.webhookSecret)
                      reveal('웹훅 서명 secret', result.webhookSecret);
                  },
                  () => complete('웹훅 설정을 저장했습니다.'),
                )
              }
            />
          )}
          {edit === 'reject' && (
            <Form
              layout="vertical"
              disabled={busy}
              onFinish={({ reason }) =>
                void run(
                  async () => {
                    await api.rejectGiftPartner(partner._id, reason.trim());
                  },
                  () => complete('요청을 반려했습니다.'),
                )
              }
            >
              <Form.Item
                name="reason"
                label="신청자에게 표시할 반려 사유"
                rules={[
                  { required: true, whitespace: true, message: '반려 사유를 입력하세요' },
                ]}
              >
                <Input.TextArea maxLength={500} showCount rows={4} />
              </Form.Item>
              <Button danger htmlType="submit" loading={busy}>
                반려
              </Button>
            </Form>
          )}
          {edit === 'issue' && (
            <Form
              layout="vertical"
              disabled={busy}
              onFinish={({ label }) =>
                void run(
                  async () => {
                    const result = await api.issueGiftPartnerKey(
                      role,
                      partner._id,
                      label,
                    );
                    reveal('연동키가 발급되었습니다', result.apiKey);
                  },
                  () => complete('연동키를 발급했습니다.'),
                )
              }
            >
              <p className="mb-4 text-sm text-gray-600">
                발급한 API 키는 다음 화면에서 한 번만 표시됩니다. 복사해서 안전하게 보관한
                뒤 개발 담당자에게 전달하세요.{' '}
                {approved
                  ? '승인된 서버 IP에서 사용할 수 있습니다.'
                  : '지금 발급해도 실제 API 사용은 관리자 승인 후 가능합니다.'}
              </p>
              <Form.Item
                name="label"
                label="키 이름 (선택)"
                extra="키를 구분하는 메모입니다. 비워 두어도 발급할 수 있습니다."
              >
                <Input placeholder="예: 운영 서버" />
              </Form.Item>
              <Button
                htmlType="submit"
                type="primary"
                className="gift-partner-primary"
                loading={busy}
              >
                발급
              </Button>
            </Form>
          )}
        </div>
      </Modal>
      <Modal
        okButtonProps={{ className: 'gift-partner-primary' }}
        open={!!confirmation}
        title={`${partner.name} · ${confirmation?.title ?? ''}`}
        onCancel={() => setConfirmation(undefined)}
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
        confirmLoading={busy}
        cancelButtonProps={{ disabled: busy }}
        okText="확인"
        cancelText="취소"
        onOk={() =>
          confirmation && void run(confirmation.task, () => complete('처리했습니다.'))
        }
      >
        <div className="space-y-3">
          <p>{confirmation?.message}</p>
          <PartnerError error={error} />
        </div>
      </Modal>
      {secret && <SecretModal secret={secret} onClose={() => setSecret(undefined)} />}
    </div>
  );
};

const WebhookForm = ({
  partner,
  admin,
  busy,
  onSubmit,
}: {
  partner: GiftPartner;
  admin: boolean;
  busy: boolean;
  onSubmit: (dto: { events: GiftPartnerWebhookEventType[]; url?: string }) => void;
}) => {
  const [all, setAll] = useState(!partner.webhook?.events?.length);
  return (
    <Form
      layout="vertical"
      disabled={busy}
      initialValues={{
        events: partner.webhook?.events ?? [],
        webhookUrl: partner.webhook?.url ?? '',
      }}
      onFinish={(v) =>
        onSubmit({ events: all ? [] : v.events, ...(admin ? { url: v.webhookUrl } : {}) })
      }
    >
      {admin && (
        <>
          <p className="text-sm text-gray-600">콜백 URL 변경은 즉시 적용됩니다.</p>
          <WebhookUrlField />
        </>
      )}
      <Form.Item label="구독 범위">
        <Radio.Group value={all} onChange={(e) => setAll(e.target.value)}>
          <Radio value={true}>전체 (권장)</Radio>
          <Radio value={false}>선택</Radio>
        </Radio.Group>
      </Form.Item>
      {!all && (
        <Form.Item
          name="events"
          rules={[
            {
              type: 'array',
              min: 1,
              required: true,
              message: '이벤트를 선택하거나 전체 구독을 사용하세요',
            },
          ]}
        >
          <Checkbox.Group options={eventOptions} />
        </Form.Item>
      )}
      <Button
        type="primary"
        className="gift-partner-primary"
        htmlType="submit"
        loading={busy}
      >
        웹훅 저장
      </Button>
    </Form>
  );
};

const WebhookUrlField = () => (
  <Form.Item
    name="webhookUrl"
    label="콜백 URL"
    rules={[
      {
        validator: async (_, v: string) => {
          const error = hookUrlError(v ?? '');
          if (error) throw new Error(error);
        },
      },
    ]}
  >
    <Input placeholder="https://partner.example.com/webhook" />
  </Form.Item>
);

const WebhookHistory = ({
  partner,
  role,
  enabled,
}: {
  partner: GiftPartner;
  role: api.GiftPartnerRole;
  enabled: boolean;
}) => {
  const { userProfile } = useAuth();
  const [status, setStatus] = useState('');
  const [limit, setLimit] = useState(50);
  const { run, busy, error } = useGiftPartnerAction();
  const query = useQuery(
    ['giftPartner', role, partner._id, 'webhookEvents', status, limit, userProfile?._id],
    async () => {
      try {
        return await api.getGiftPartnerWebhookEvents(role, partner._id, {
          ...(status ? { status } : {}),
          limit,
        });
      } catch (e) {
        throw new Error(api.giftPartnerError(e));
      }
    },
    { retry: false },
  );
  return (
    <PartnerSection title="웹훅 발송 이력">
      <div className="flex flex-wrap gap-2">
        <Select
          aria-label="발송 상태"
          value={status}
          onChange={setStatus}
          options={[
            { value: '', label: '전체' },
            { value: 'Pending', label: '재시도 대기' },
            { value: 'Failed', label: '실패' },
            { value: 'Delivered', label: '전달 완료' },
          ]}
          className="w-36"
        />
        <Select
          aria-label="조회 건수"
          value={limit}
          onChange={setLimit}
          options={[50, 100, 200].map((value) => ({ value, label: `최근 ${value}건` }))}
        />
        <Button onClick={() => void query.refetch()} loading={query.isFetching}>
          새로고침
        </Button>
      </div>
      <PartnerError
        error={
          error ||
          (query.isError ? '발송 이력을 불러오지 못했습니다. 다시 시도해 주세요.' : '')
        }
      />
      {query.isLoading && <p>이력을 불러오는 중입니다.</p>}
      {query.data?.length === 0 && (
        <p className="text-sm text-gray-500">발송 이력이 없습니다.</p>
      )}
      <div className="space-y-2">
        {query.data?.map((event) => (
          <div
            key={event.eventId}
            className="space-y-2 rounded border border-gray-200 p-3 text-sm"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong>
                {eventOptions.find((o) => o.value === event.event)?.label ??
                  '테스트 발송'}
              </strong>
              <Tag
                color={
                  event.status === 'Delivered'
                    ? 'green'
                    : event.status === 'Failed'
                    ? 'red'
                    : 'orange'
                }
              >
                {event.status === 'Delivered'
                  ? '전달 완료'
                  : event.status === 'Failed'
                  ? '6회 실패로 중단'
                  : '재시도 대기'}
              </Tag>
            </div>
            <p className="break-all">
              {event.eventId} · 배송{' '}
              {event.deliveries?.map((d) => `${d.code}-${d.seq}`).join(', ') || '—'}
            </p>
            <p>
              생성 {dateLabel(event.createdAt)} · {event.attempts}회 시도 · HTTP{' '}
              {event.lastResponseStatus ?? '—'}
            </p>
            <p>
              마지막 시도 {dateLabel(event.lastAttemptAt)} · 다음 시도{' '}
              {dateLabel(event.nextAttemptAt)} · 전달 {dateLabel(event.deliveredAt)}
            </p>
            {event.lastError && (
              <p className="break-all text-red-600">{event.lastError}</p>
            )}
            {event.status !== 'Delivered' && (
              <Button
                size="small"
                disabled={!enabled || busy}
                onClick={() =>
                  void run(async () => {
                    await api.resendGiftPartnerWebhookEvent(
                      role,
                      partner._id,
                      event.eventId,
                    );
                  })
                }
              >
                재발송
              </Button>
            )}
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-500">
        자동 재시도: 1분·5분·30분·2시간·6시간 (한국 시간 표시)
      </p>
    </PartnerSection>
  );
};

const ApplicantWebhookSection = ({
  collapsed,
  children,
}: {
  collapsed: boolean;
  children: ReactNode;
}) =>
  collapsed ? (
    <details className="rounded-lg border border-gray-200 bg-white p-4">
      <summary className="cursor-pointer text-sm font-semibold">
        웹훅 알림 설정 · 관리자 승인 후 사용 (선택)
      </summary>
      <p className="my-3 text-sm text-gray-600">
        발주 알림을 서버로 자동 전송하는 기능입니다. 지금은 설정하지 않아도 됩니다. 승인
        후 필요할 때 켜세요.
      </p>
      <div className="space-y-4">{children}</div>
    </details>
  ) : (
    <>{children}</>
  );
